import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lock, Stethoscope, Truck, Upload, UserPlus,
  ClipboardList, Receipt, Building2, CheckCircle2, BarChart3, FileSpreadsheet,
  HeartHandshake,
} from "lucide-react";

const img = (id, w = 900) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

const STEPS = [
  { icon: Upload, title: "Submit a referral", desc: "Your team sends us a member and pet through your secure partner portal, one at a time or in bulk. Every field is encrypted in transit and at rest." },
  { icon: UserPlus, title: "We onboard your member", desc: "PawLinx creates the member's account automatically and emails them an encrypted link to set their password. No paperwork on your side." },
  { icon: Stethoscope, title: "Member requests their medication", desc: "The member logs in and requests their meds. A licensed veterinarian reviews every prescription before a licensed pharmacy fills it." },
  { icon: Receipt, title: "You're billed the agreed fee", desc: "Members pay nothing at the point of care. You're billed the medication fee we agreed on, on the schedule you choose: instant, weekly, biweekly, or monthly." },
];

const BENEFITS = [
  { icon: Lock, title: "Encrypted end to end", desc: "Member and pet data is encrypted the moment you submit it and stays encrypted at rest." },
  { icon: FileSpreadsheet, title: "Bulk referrals", desc: "Send one member or upload hundreds at once. The portal handles the rest." },
  { icon: BarChart3, title: "Real time status", desc: "Track every referral from submission to delivery from a single dashboard." },
  { icon: ShieldCheck, title: "Vet reviewed", desc: "A licensed veterinarian signs off on every prescription before it is filled." },
  { icon: Receipt, title: "Predictable costs", desc: "One medication rate, agreed with you up front and billed weekly, biweekly, or monthly. No surprises." },
  { icon: Truck, title: "Delivered across the United States", desc: "Licensed pharmacies ship directly to your members' doors." },
];

const FEATURE_POINTS = [
  "Zero build cost and no software for your team to maintain",
  "Your members keep their existing benefits, we just lower the cost of their pet's meds",
  "Every prescription reviewed by a licensed veterinarian",
  "One simple invoice on the cadence you choose",
];

export default function InsuranceLandingPage() {
  return (
    <div className="bg-paw-cream">
      {/* HERO */}
      <section className="bg-white pt-14 pb-20 overflow-x-clip">
        <div className="max-w-[1180px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">
              <Building2 className="w-4 h-4" /> PawLinx for Insurance Partners
            </span>
            <h1 className="mt-3 text-3xl sm:text-[52px] font-extrabold tracking-tight text-paw-navy leading-[1.05]">
              Lower your members' pet medication costs. We handle everything else.
            </h1>
            <p className="mt-5 text-lg text-paw-text-secondary">
              Refer a member and pet, and PawLinx takes it from there: account setup, vet review, licensed pharmacy fulfillment, and delivery across the United States. Your members pay nothing at the point of care, and you're billed an agreed medication fee on your own schedule.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/for-insurance/apply" className="inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-7 py-3.5 font-semibold transition-all shadow-[0_8px_20px_-8px_rgba(15,98,214,0.6)]">
                Become a partner <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/partner/login" className="inline-flex items-center justify-center gap-2 bg-white border border-paw-border text-paw-navy rounded-full px-7 py-3.5 font-semibold hover:border-paw-text-muted transition-all">
                Partner login
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-paw-text-secondary">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-paw-primary" /> Encrypted end to end</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-paw-primary" /> Vet reviewed</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-paw-primary" /> US delivery</span>
            </div>
          </div>
          {/* Layered photo collage */}
          <div className="relative h-[440px] hidden lg:block">
            <div className="absolute right-0 top-0 w-[78%] h-[300px] rounded-[28px] overflow-hidden shadow-[0_40px_90px_-34px_rgba(11,39,71,0.45)] bg-cover bg-center" style={{ backgroundImage: `url(${img("photo-1576201836106-db1758fd1c97")})` }} />
            <div className="absolute left-0 bottom-0 w-[52%] h-[240px] rounded-[24px] overflow-hidden ring-8 ring-white shadow-[0_30px_70px_-30px_rgba(11,39,71,0.4)] bg-cover bg-center" style={{ backgroundImage: `url(${img("photo-1543466835-00a7907e9de1")})` }} />
            <div className="absolute right-2 bottom-6 bg-white rounded-2xl shadow-xl px-5 py-4 border border-paw-border">
              <div className="text-2xl font-extrabold text-paw-navy">Up to 30%</div>
              <div className="text-xs text-paw-text-secondary">lower medication cost</div>
            </div>
            <svg className="absolute left-[46%] top-[-10px] w-10 h-10" viewBox="0 0 40 40" aria-hidden="true"><path d="M20 2 L23 16 L37 20 L23 24 L20 38 L17 24 L3 20 L17 16 Z" fill="#EBB13E" /></svg>
          </div>
          {/* Mobile single image */}
          <div className="relative lg:hidden rounded-[28px] overflow-hidden shadow-xl aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: `url(${img("photo-1576201836106-db1758fd1c97")})` }} />
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-paw-navy text-white">
        <div className="max-w-[1180px] mx-auto px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[["Up to 30%", "average member savings"], ["12,000+", "pets helped"], ["100%", "vet reviewed"], ["United States", "delivery"]].map(([b, s]) => (
            <div key={s}>
              <div className="text-2xl sm:text-3xl font-extrabold tracking-tight">{b}</div>
              <div className="text-[#9fb6cf] text-xs sm:text-sm mt-1">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SPLIT FEATURE */}
      <section className="py-20">
        <div className="max-w-[1180px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="rounded-[32px] overflow-hidden shadow-[0_40px_90px_-34px_rgba(11,39,71,0.45)] aspect-[5/4] bg-cover bg-center" style={{ backgroundImage: `url(${img("photo-1548199973-03cce0bbc87b")})` }} />
            <div className="absolute -bottom-6 -right-4 hidden sm:flex items-center gap-3 bg-white rounded-2xl shadow-xl px-5 py-4 border border-paw-border">
              <span className="w-10 h-10 rounded-xl bg-paw-blue-50 grid place-items-center text-paw-primary"><HeartHandshake className="w-5 h-5" /></span>
              <div>
                <div className="text-sm font-bold text-paw-navy">Turnkey benefit</div>
                <div className="text-xs text-paw-text-secondary">Live in days, not months</div>
              </div>
            </div>
          </div>
          <div>
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">A pet pharmacy benefit, done for you</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy leading-tight">Add real value for your members without building anything</h2>
            <p className="mt-4 text-paw-text-secondary">PawLinx runs the entire pet medication journey on your behalf. You refer, we deliver, and your members feel the savings.</p>
            <div className="mt-6 space-y-3">
              {FEATURE_POINTS.map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-paw-primary shrink-0 mt-0.5" />
                  <span className="text-[15px] text-paw-text">{p}</span>
                </div>
              ))}
            </div>
            <Link to="/for-insurance/apply" className="mt-7 inline-flex items-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-7 py-3.5 font-semibold transition-all">Become a partner <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-insurance" className="pb-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">How it works</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Four steps, almost no work on your side</h2>
            <p className="mt-4 text-paw-text-secondary">From referral to delivery, PawLinx runs the whole flow. Your team submits, we do the rest.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative bg-white border border-paw-border rounded-3xl p-6 shadow-sm">
                <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-paw-navy text-white grid place-items-center text-sm font-bold shadow-md">{i + 1}</div>
                <div className="w-12 h-12 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-4"><s.icon className="w-6 h-6" /></div>
                <h3 className="text-lg font-bold text-paw-navy mb-1.5">{s.title}</h3>
                <p className="text-sm text-paw-text-secondary leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR YOUR MEMBERS */}
      <section className="pb-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-0 rounded-[32px] overflow-hidden border border-paw-border shadow-sm bg-white">
            <div className="p-8 sm:p-12 order-2 lg:order-1">
              <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">For your members</span>
              <h2 className="mt-3 text-3xl sm:text-[34px] font-extrabold tracking-tight text-paw-navy leading-tight">A simple, caring experience from start to finish</h2>
              <p className="mt-4 text-paw-text-secondary">Your members get an effortless way to keep their pets healthy. They set up their account in one click, request their medication, and it arrives at their door, with nothing to pay at the point of care.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {[
                  ["No cost at point of care", Receipt],
                  ["Vet reviewed prescriptions", ShieldCheck],
                  ["Delivered to the door", Truck],
                  ["Reorder in one click", HeartHandshake],
                ].map(([t, Icon]) => (
                  <div key={t} className="flex items-center gap-3 bg-paw-cream rounded-2xl px-4 py-3">
                    <span className="w-9 h-9 rounded-xl bg-white grid place-items-center text-paw-primary shadow-sm"><Icon className="w-5 h-5" /></span>
                    <span className="text-sm font-medium text-paw-navy">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 min-h-[280px] bg-cover bg-center" style={{ backgroundImage: `url(${img("photo-1450778869180-41d0601e046e", 1100)})` }} />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="pb-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">Why partner with PawLinx</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Built for insurers, designed for members</h2>
          </div>
          <div className="mt-11 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-white border border-paw-border rounded-3xl p-6 shadow-sm">
                <div className="w-12 h-12 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-4"><b.icon className="w-6 h-6" /></div>
                <h3 className="text-lg font-bold text-paw-navy mb-1.5">{b.title}</h3>
                <p className="text-sm text-paw-text-secondary leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="pb-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center bg-paw-navy text-white rounded-[32px] p-8 sm:p-12 overflow-hidden relative">
            <div className="absolute -right-24 -top-28 w-80 h-80 rounded-full bg-white/5" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-gold"><Lock className="w-4 h-4" /> Security first</span>
              <h2 className="mt-3 text-3xl sm:text-[34px] font-extrabold tracking-tight">Your members' data is protected at every step</h2>
              <p className="mt-4 text-[15px] text-white/70 leading-relaxed">Referrals are encrypted the moment your team submits them and stay encrypted at rest. Members receive a private, expiring link to set their own password, so credentials are never shared in the clear.</p>
            </div>
            <div className="relative grid gap-3">
              {[
                "Encrypted submission and storage of all member and pet data",
                "Private, expiring set password links for every member",
                "Role based access for your team inside the partner portal",
                "Full status trail from referral to delivery",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 bg-white/5 rounded-2xl px-4 py-3">
                  <CheckCircle2 className="w-5 h-5 text-paw-gold shrink-0 mt-0.5" />
                  <span className="text-sm text-white/85">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="relative overflow-hidden rounded-[32px] bg-paw-primary text-white grid lg:grid-cols-2">
            <div className="p-10 sm:p-14">
              <ClipboardList className="w-10 h-10 mb-4 text-white/90" />
              <h2 className="text-3xl sm:text-[38px] font-extrabold tracking-tight">Ready to lower costs for your members?</h2>
              <p className="mt-4 text-[17px] text-[#cfe0f2]">Tell us about your organization and our team will set you up with a partner account. Already a partner? Log in to submit and track referrals.</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/for-insurance/apply" className="inline-flex items-center justify-center gap-2 bg-white text-paw-navy rounded-full px-7 py-3.5 font-semibold hover:bg-paw-cream transition-all">
                  Become a partner <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/partner/login" className="inline-flex items-center justify-center gap-2 bg-paw-navy/30 border border-white/30 text-white rounded-full px-7 py-3.5 font-semibold hover:bg-paw-navy/50 transition-all">
                  Partner login
                </Link>
              </div>
            </div>
            <div className="relative min-h-[260px] bg-cover bg-center hidden lg:block" style={{ backgroundImage: `url(${img("photo-1517849845537-4d257902454a", 1000)})` }}>
              <div className="absolute inset-0 bg-gradient-to-r from-paw-primary via-paw-primary/30 to-transparent" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
